import { ProductThumb } from '@/components/products/ProductThumb';
import { entityLinkClassName } from '@/lib/entityLink';
import { productDetailPath } from '@/lib/entityPaths';
import { productThumbnailUrl, type ProductImageSource } from '@/lib/productImage';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

const thumbSizeClasses = {
  sm: 'h-10 w-10',
  md: 'h-14 w-14',
  lg: 'h-full min-h-14 w-20',
} as const;

export interface ProductIdentityProps {
  name: string;
  sku?: string | null;
  /** Direct image URL; ignored when `product` resolves a thumbnail. */
  imageUrl?: string | null;
  product?: ProductImageSource | null;
  /** When set, links to the product detail page unless `href` or `onClick` is provided. */
  productId?: string;
  href?: string;
  /** When set, the identity is a button and does not navigate. */
  onClick?: () => void;
  size?: keyof typeof thumbSizeClasses;
  /** Show SKU under the name when it differs from the name. Default true. */
  showSku?: boolean;
  popOut?: boolean;
  className?: string;
  /** Stacked under the name (not inside the product link). */
  children?: ReactNode;
  /** Shown beside the name, e.g. a status badge. */
  trailing?: ReactNode;
}

function resolveImageUrl(
  imageUrl: string | null | undefined,
  product: ProductImageSource | null | undefined
): string | null {
  if (product) return productThumbnailUrl(product);
  const direct = typeof imageUrl === 'string' ? imageUrl.trim() : '';
  return direct || null;
}

export function ProductIdentity({
  name,
  sku,
  imageUrl,
  product,
  productId,
  href,
  onClick,
  size = 'md',
  showSku = true,
  popOut = true,
  className,
  children,
  trailing,
}: ProductIdentityProps) {
  const resolvedName = name.trim() || '—';
  const resolvedSku = sku?.trim() || '';
  const showSkuLine =
    showSku && Boolean(resolvedSku) && resolvedSku !== resolvedName;
  const src = resolveImageUrl(imageUrl, product);
  const stacked = children != null || trailing != null;
  const linkHref =
    href ?? (productId?.trim() ? productDetailPath(productId.trim()) : undefined);

  const thumb = (
    <div
      className={cn(
        'relative shrink-0 overflow-hidden rounded-md',
        thumbSizeClasses[stacked ? 'lg' : size],
        stacked && 'self-stretch bg-muted/30'
      )}
    >
      <ProductThumb imageUrl={src} popOut={popOut} />
    </div>
  );

  const title = (
    <>
      <div className="font-medium break-words leading-snug">{resolvedName}</div>
      {showSkuLine && (
        <div className="text-sm text-muted-foreground font-mono break-all leading-snug mt-0.5">
          {resolvedSku}
        </div>
      )}
    </>
  );

  function asInteractive(node: ReactNode, extraClass?: string) {
    if (onClick) {
      return (
        <button
          type="button"
          onClick={onClick}
          className={cn(
            entityLinkClassName,
            'bg-transparent p-0 text-left',
            extraClass
          )}
        >
          {node}
        </button>
      );
    }
    if (linkHref) {
      return (
        <Link to={linkHref} className={cn(entityLinkClassName, extraClass)}>
          {node}
        </Link>
      );
    }
    return extraClass ? <div className={extraClass}>{node}</div> : node;
  }

  if (stacked) {
    return (
      <div className={cn('flex min-w-0 items-stretch gap-3', className)}>
        {asInteractive(thumb, 'flex shrink-0 self-stretch')}
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex items-start gap-2">
            {asInteractive(<div className="min-w-0">{title}</div>, 'min-w-0 flex-1')}
            {trailing}
          </div>
          {children}
        </div>
      </div>
    );
  }

  const content = (
    <>
      {thumb}
      <div className="min-w-0 flex-1">{title}</div>
    </>
  );

  const rootClass = cn(
    'flex gap-3 min-w-0',
    showSkuLine ? 'items-start' : 'items-center',
    className
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(rootClass, entityLinkClassName, 'bg-transparent p-0 text-left')}
      >
        {content}
      </button>
    );
  }

  if (linkHref) {
    return (
      <Link to={linkHref} className={cn(rootClass, entityLinkClassName)}>
        {content}
      </Link>
    );
  }

  return <div className={rootClass}>{content}</div>;
}
